import { z } from "zod";

/**
 * `SYNTHESIS-FIELDS` (WF-BNK-04 step 4, lane A's A-1b-2b): **the typed facts the bank's synthesis prints, on `GET /api/v1/bank/files/{id}`**, under the key
 * `synthesis`. Stored facts only: nothing is computed or compared in the browser, a fact the file does not hold is `null` WITH its cause (never 0, never a dash
 * the screen invents), and no internal id travels (the dealership is named, never numbered). The client's employer and contract are the profile FROZEN at the
 * send (`lending.submission_profile_snapshots`), the same copy every step of the run read; a file older than the snapshot reads the live profile and says so.
 *
 * The apport in francs is DERIVED from the stored percent and the price by the buyer's own quote function, and labelled so; the dossier stores no francs amount.
 */

/** Why a fact is absent, in the bank's words. */
export const synthesisNotReadSchema = z.strictObject({ cause: z.string(), words: z.string() });

/** One declared employment as the send froze it, without its id and without its salary (the figures card holds the net). */
export const synthesisEmploymentSchema = z.strictObject({
  employer: z.string(),
  contractType: z.enum(["fonctionnaireTitulaire", "cdi", "cdd", "contractuelEtat", "autre"]),
  /** `YYYY-MM-DD`, the borrower's declaration; the alternate of `declaredSeniorityMonths` (either may be null). */
  dateOfHire: z.string().nullable(),
  declaredSeniorityMonths: z.number().int().nullable(),
});

export const bankFileSynthesisSchema = z.strictObject({
  employments: z.strictObject({
    /** `snapshot` for every submission born after the freeze, `live` (with `note`) for an older one; `null` when no profile could be read. */
    source: z.enum(["snapshot", "live"]).nullable(),
    note: z.string().nullable(),
    items: z.array(synthesisEmploymentSchema),
    notRead: synthesisNotReadSchema.nullable(),
  }),
  /** The vehicle's price as `catalog` holds it, the one the disbursement splits; whole FCFA. */
  vehiclePrice: z.strictObject({
    amount: z.number().int().nullable(),
    notRead: synthesisNotReadSchema.nullable(),
  }),
  /**
   * The apport in francs, DERIVED and labelled so (`basis`): the price less the amount financed at the deposit percent the buyer declared, by the one function the
   * buyer's own quote uses (`montantFinanceMirror`, equal to the simulation's `montantFinance` by spec). `pct` is the stored percent (`dossiers.deposit_pct`).
   */
  apport: z.strictObject({
    amount: z.number().int().nullable(),
    basis: z.literal("depuis le pourcentage déclaré").nullable(),
    pct: z.number().int().nullable(),
    notRead: synthesisNotReadSchema.nullable(),
  }),
  /** The selling dealership's organisation name (`identity`'s), never its id. */
  seller: z.strictObject({ name: z.string().nullable(), notRead: synthesisNotReadSchema.nullable() }),
  /**
   * The buyer's decision on a warranty the bank did not finance (WF-CLI-23): kept and settled cash, or withdrawn. `state` (`WARRANTY-DECISION-STATE`) is the file's
   * warranty told in four, from stored facts only: `none` (no warranty proposed, or the bank financed it: nothing to decide), `pending` (a warranty the bank did
   * not finance and no decision yet), `comptant`, `retiree` (the decision, as `dossiers.warranty_settlement` holds it). The browser never compares an amount with 0.
   */
  warrantySettlement: z.strictObject({
    state: z.enum(["none", "pending", "comptant", "retiree"]),
    /**
     * `WARRANTY-SUBSCRIBED` (pilot, D-1025-AA-5): whether the file carries a mechanical warranty at all, told by the SERVER from the offer's stored amount, so the
     * browser never compares one with 0. True iff the client's retained offer (else this bank's own offer on the file) carries `amount_warranty > 0`; « retiree » keeps it
     * true (the amount survives the decision in code). False when that offer carries none (« non souscrite »). NULL when there is no retained offer and no offer of this bank (`WARRANTY-SUBSCRIBED`, amended by record: both texts kept, the first said « never null »): the card renders at every stage, and « non souscrite » before anyone could subscribe would be a false statement. The
     * client's own choice (WF-CLI-12) is booked whole as WARRANTY-SELECTION, after the pilot.
     */
    subscribed: z.boolean().nullable(),
    value: z.enum(["comptant", "retiree"]).nullable(),
    notRead: synthesisNotReadSchema.nullable(),
  }),
});

export type BankFileSynthesis = z.infer<typeof bankFileSynthesisSchema>;
export type SynthesisNotRead = z.infer<typeof synthesisNotReadSchema>;
